import { useMemo, useState, useCallback, useEffect } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { parseUnits, type Address } from 'viem';
import { useChainId } from 'wagmi';

import { calculateTokensRemaining } from '@/app/[locale]/(main)/(home)/_components/Pools/tokenDataUtils';
import { useBuyTokens } from '@/hooks/useBuyTokens';
import { useNumberInputValidation } from '@/hooks/useNumberInputValidation';
import { usePersonaKYC } from '@/hooks/usePersonaKYC';
import { useSellTokens } from '@/hooks/useSellTokens';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { formatPriceInput } from '@/lib/utils/formatPriceInput';
import { removeCommas } from '@/lib/utils/removeCommas';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { C_POOL_TOKEN_DECIMALS } from '@/ts/constants/C_DECIMAL_PLACES';
import { C_ACTIVE_NETWORK_ID } from '@/ts/constants/C_NETWORKS';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';

import type { T_Pool } from '@/ts/types/T_Pool';

export type T_SwapDirection = 'usdc-to-token' | 'token-to-usdc';

interface I_UsePoolsTokenSwapProps {
  pool: T_Pool;
  poolContractAddress: Address;
  tokenAddress: Address;
  tokenDecimals: number;
  refetchBalances: () => Promise<unknown[]>;
  t: (key: string) => string;
  isConnected: boolean;
  isSuspended: boolean;
  openWalletConnect: () => void;
}

export function usePoolsTokenSwap({
  pool,
  poolContractAddress,
  tokenAddress,
  tokenDecimals,
  refetchBalances,
  t,
  isConnected,
  isSuspended,
  openWalletConnect
}: I_UsePoolsTokenSwapProps) {
  const chainId = useChainId();
  const globalUser = useGlobalData('user');
  const activeChainId = isConnected ? chainId : C_ACTIVE_NETWORK_ID;

  const [swapDirection, setSwapDirection] = useState<T_SwapDirection>(
    isSuspended ? 'token-to-usdc' : 'usdc-to-token'
  );
  const [inputValue, setInputValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isInputPendingCalculation, setIsInputPendingCalculation] =
    useState(false);
  const [isUpdatingInputFromCalculation, setIsUpdatingInputFromCalculation] =
    useState(false);

  const tPredictions = useTranslations('predictions');
  const {
    needsKYC,
    isKycLoading,
    openPersonaModal,
    remainingAmount,
    isKycRestrictionsEnabled
  } = usePersonaKYC();

  // Input validation
  const {
    value: validatedInput,
    handleInputChange,
    handleFocusOut,
    reset: resetInput
  } = useNumberInputValidation({
    onValidValueChange: () => {},
    decimalPlaces:
      swapDirection === 'usdc-to-token'
        ? C_USDC_DECIMALS
        : C_POOL_TOKEN_DECIMALS
  });

  // Calculate input amount as BigInt
  const inputAmountBigInt = useMemo(() => {
    if (!validatedInput) return undefined;
    const cleaned = removeCommas(validatedInput);
    if (!cleaned || parseFloat(cleaned) <= 0) return undefined;
    try {
      if (swapDirection === 'usdc-to-token') {
        return parseUnits(cleaned, C_USDC_DECIMALS);
      } else {
        // Parse with display decimals (C_POOL_TOKEN_DECIMALS), then convert to contract decimals
        const parsedWithDisplayDecimals = parseUnits(
          cleaned,
          C_POOL_TOKEN_DECIMALS
        );
        // Convert from display decimals to contract decimals
        if (tokenDecimals > C_POOL_TOKEN_DECIMALS) {
          return (
            parsedWithDisplayDecimals *
            BigInt(10 ** (tokenDecimals - C_POOL_TOKEN_DECIMALS))
          );
        } else if (tokenDecimals < C_POOL_TOKEN_DECIMALS) {
          return (
            parsedWithDisplayDecimals /
            BigInt(10 ** (C_POOL_TOKEN_DECIMALS - tokenDecimals))
          );
        }
        return parsedWithDisplayDecimals;
      }
    } catch {
      return undefined;
    }
  }, [validatedInput, swapDirection, tokenDecimals]);

  // Hooks for buy/sell
  const { buyTokens, state: buyState } = useBuyTokens(poolContractAddress);
  const { sellTokens, state: sellState } = useSellTokens(
    poolContractAddress,
    tokenAddress,
    tokenDecimals
  );

  const isFullyConnected = isConnected && !!globalUser;

  // Force token-to-usdc if suspended state becomes true (e.g. from async KYC hook)
  useEffect(() => {
    if (isSuspended && swapDirection !== 'token-to-usdc') {
      setSwapDirection('token-to-usdc');

      // Also reset input formatting when forced to token side
      const displayDecimals = C_POOL_TOKEN_DECIMALS;
      if (validatedInput) {
        const currentValue = removeCommas(validatedInput);
        const numericValue = parseFloat(currentValue);
        if (!isNaN(numericValue) && numericValue > 0) {
          const reformattedValue = formatPriceInput(
            numericValue.toString(),
            displayDecimals
          );
          handleInputChange(reformattedValue);
          setInputValue(reformattedValue);
        }
        setIsInputPendingCalculation(true);
      }
    }
  }, [isSuspended, swapDirection, validatedInput, handleInputChange]);

  // Handle swap direction change
  const handleSwapDirection = useCallback(() => {
    const newDirection: T_SwapDirection =
      swapDirection === 'usdc-to-token' ? 'token-to-usdc' : 'usdc-to-token';
    const newDecimals =
      newDirection === 'usdc-to-token'
        ? C_USDC_DECIMALS
        : C_POOL_TOKEN_DECIMALS;

    setSwapDirection(newDirection);

    // Reformat input value with new decimals if there's a value
    if (validatedInput) {
      const currentValue = removeCommas(validatedInput);
      const numericValue = parseFloat(currentValue);
      if (!isNaN(numericValue) && numericValue > 0) {
        const displayDecimals =
          newDirection === 'usdc-to-token'
            ? C_USDC_DECIMALS
            : C_POOL_TOKEN_DECIMALS;
        const reformattedValue = formatPriceInput(
          numericValue.toString(),
          displayDecimals
        );
        handleInputChange(reformattedValue);
        setInputValue(reformattedValue);
      }
      setIsInputPendingCalculation(true);
    }
  }, [validatedInput, swapDirection, handleInputChange]);

  // Update input value formatting when swap direction changes (for empty/zero values)
  // Only set default when not focused, to allow users to clear the input
  useEffect(() => {
    if (isInputFocused) return; // Don't interfere when user is typing

    const currentValue = removeCommas(validatedInput || '');
    const numericValue = parseFloat(currentValue);

    if (
      !validatedInput ||
      validatedInput === '' ||
      (numericValue === 0 && !isNaN(numericValue))
    ) {
      const defaultValue = formatNumberWithCommasAndDecimals(
        0,
        swapDirection === 'usdc-to-token'
          ? C_USDC_DECIMALS
          : C_POOL_TOKEN_DECIMALS
      );
      handleInputChange(defaultValue);
      setInputValue(defaultValue);
    }
  }, [
    swapDirection,
    tokenDecimals,
    handleInputChange,
    validatedInput,
    isInputFocused
  ]);

  // Trigger recalculation when swap direction changes
  useEffect(() => {
    if (validatedInput && !isInputFocused) {
      setIsInputPendingCalculation(true);
    }
  }, [swapDirection, validatedInput, isInputFocused]);

  // Check if tokens are finished
  const tokensRemaining = calculateTokensRemaining(pool);
  const tokensRemainingNum = parseFloat(tokensRemaining) || 0;
  const isTokensFinished = tokensRemainingNum <= 0;

  // Handle swap execution
  const handleSwap = useCallback(async () => {
    if (!validatedInput || parseFloat(removeCommas(validatedInput)) <= 0) {
      toast.error(t('enterValidAmount'));
      return;
    }

    if (!isConnected || !globalUser) {
      openWalletConnect();
      return;
    }

    // Check if tokens are finished when trying to buy
    if (swapDirection === 'usdc-to-token') {
      const currentTokensRemaining = calculateTokensRemaining(pool);
      const currentTokensRemainingNum = parseFloat(currentTokensRemaining) || 0;
      if (currentTokensRemainingNum <= 0) {
        toast.error(t('tokensSoldOut'));
        return;
      }

      // Check remaining KYC allowance before buying
      const usdcAmount = parseFloat(removeCommas(validatedInput)) || 0;
      if (
        isKycRestrictionsEnabled &&
        !needsKYC &&
        remainingAmount > 0 &&
        usdcAmount > remainingAmount
      ) {
        toast.error(
          tPredictions('kycRemainingExceeded', {
            remaining: remainingAmount.toLocaleString()
          })
        );
        return;
      }
    }

    try {
      if (swapDirection === 'usdc-to-token') {
        const result = await buyTokens(validatedInput);
        if (result.success) {
          toast.success(t('tokensBoughtSuccessfully'));
          await refetchBalances();
          resetInput();
          setInputValue('');
        } else {
          toast.error(result.error || t('buyFailed'));
        }
      } else {
        const result = await sellTokens(validatedInput);
        if (result.success) {
          toast.success(t('tokensSoldSuccessfully'));
          await refetchBalances();
          resetInput();
          setInputValue('');
        } else {
          toast.error(result.error || t('sellFailed'));
        }
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('transactionFailed')
      );
    }
  }, [
    validatedInput,
    swapDirection,
    isConnected,
    globalUser,
    buyTokens,
    sellTokens,
    openWalletConnect,
    refetchBalances,
    resetInput,
    t,
    pool
  ]);

  // Sync input value
  useEffect(() => {
    setInputValue(validatedInput);
  }, [validatedInput]);

  const isBusy =
    buyState.isLoading ||
    buyState.isApproving ||
    buyState.isBuying ||
    sellState.isLoading ||
    sellState.isApproving ||
    sellState.isSelling;

  return {
    swapDirection,
    inputValue,
    validatedInput,
    inputAmountBigInt,
    isInputFocused,
    isInputPendingCalculation,
    isUpdatingInputFromCalculation,
    isFullyConnected,
    isBusy,
    buyState,
    sellState,
    activeChainId,
    isTokensFinished,
    handleSwapDirection,
    handleSwap,
    handleInputChange,
    handleFocusOut,
    setIsInputFocused,
    setIsInputPendingCalculation,
    setIsUpdatingInputFromCalculation,
    setInputValue,
    needsKYC,
    isKycLoading,
    openPersonaModal
  };
}
